import thunk from 'redux-thunk';
import apiClient from '../config/axiosConfig';
import * as types from './actionTypes';
import { handleError } from '../errors/handleError';
import * as projectVersionApi from '../apis/ProjectVersionApi';
import * as projectConfigApi from '../apis/ProjectConfigApi';
import { getMedia } from '../apis/MediaApi';
import { addAlert, clearAlert } from './alertActions';
import { getLiveProjectConfig, getProjects } from './projectActions';
import { checkProjectConfig } from './projectConfigActions';
import { getPagesforProject } from './contentActions';
import { getProjectVersionMenuItems } from './menuItemActions';

export function errorHandler(dispatch, error, type) {
    const errorMessage = handleError(error);

    dispatch({
        type,
        error: errorMessage,
    });
}

export function getProjectVersion(projectId) {
    return function (dispatch) {
        dispatch({
            type: types.GET_PROJECT_VERSION_PENDING,
        });
        return projectVersionApi.getLatestProjectVersionViaApi(projectId)
            .then((response) => {
                return Promise.all([dispatch({
                    type: types.GET_PROJECT_VERSION_SUCCESS,
                    payload: response.data.data,
                }), dispatch(checkProjectConfig(projectId))]);
            })
            .catch((error) => {
                errorHandler(dispatch, error.response, types.GET_PROJECT_VERSION_FAILED);
            });
    };
}
export function updateProjectVersion(projectId, projectVersionModel) {
    return function (dispatch) {
        dispatch({
            type: types.UPDATE_PROJECT_VERSION_PENDING,
        });
        return projectVersionApi.updateProjectVersionViaApi(projectVersionModel, projectId)
            .then((response) => {
                return Promise.all([
                    dispatch({
                        type: types.UPDATE_PROJECT_VERSION_SUCCESS,
                        payload: response.data.data,
                    }),
                    dispatch(checkProjectConfig(projectId)),
                    dispatch({
                        type: types.SHOW_NOTIFICATION,
                    }),
                    setTimeout(() => {
                        dispatch({ type: types.HIDE_NOTIFICATION });
                    }, 7000)]);
            })
            .catch((error) => {
                errorHandler(dispatch, error.response, types.UPDATE_PROJECT_VERSION_FAILED);
            });
    };
}

export function updateProjectVersionForSermonDisconnect(projectId, projectVersionModel) {
    return function (dispatch) {
        dispatch({
            type: types.DISCONNECT_MEDIA_PENDING,
        });
        return dispatch(updateProjectVersion(projectId, projectVersionModel))
            .then(() => {
                return dispatch({
                    type: types.DISCONNECT_MEDIA_SUCCESS,
                });
            })
            .catch((error) => {
                errorHandler(dispatch, error.response, types.DISCONNECT_MEDIA_FAILED);
            });
    };
}

export function updateProjectVersionbyParameter(projectId, parameterName, parameterValue) {
    return function (dispatch) {
        dispatch({
            type: types.UPDATE_PROJECT_VERSION_PARAMETER_PENDING,
        });
        return apiClient.put(`/v2/projects/${projectId}/singlelatest`, {
            parameterName,
            parameterValue,
        })
            .then((response) => {
                return Promise.all([
                    dispatch({
                        type: types.UPDATE_PROJECT_VERSION_PARAMETER_SUCCESS,
                        payload: response.data.data,
                    }),
                    dispatch(checkProjectConfig(projectId)),
                    dispatch({
                        type: types.SHOW_NOTIFICATION,
                    }),
                    setTimeout(() => {
                        dispatch({ type: types.HIDE_NOTIFICATION });
                    }, 7000),
                ]);
            })
            .catch((response) => {
                errorHandler(
                    dispatch,
                    response.response,
                    types.UPDATE_PROJECT_VERSION_PARAMETER_FAILED,
                );
            });
    };
}

export function togglePreviewShown(currentState) {
    return {
        type: types.TOGGLE_PREVIEW_SHOWN,
        payload: !currentState,
    };
}

export function updateProjectVersionWithMedia(projectId, projectVersionIn) {
    return function (dispatch) {
        let projectVersionModel = projectVersionIn;
        dispatch({
            type: types.UPDATE_PROJECT_VERSION_PENDING,
        });

        if (projectVersionModel.mediaType !== 4 && projectVersionModel.cloverAccountProduct) {
            projectVersionModel.cloverAccountProduct = '';
            projectVersionModel.cloverAccountEmail = '';
        }
        let version = {};
        let media = {};

        const storeUpdatedVersion = (response) => {
            version = response.data.data;
        };

        const throwMediaError = (err) => {
            let error = err;
            dispatch({
                type: types.GET_PROJECT_VERSION_FAILED,
            });
            dispatch({
                type: types.GET_PROJECT_MEDIA_FAILED,
            });
            if (typeof error !== 'string') {
                error = error.message;
            }
            dispatch(addAlert({
                message: error,
                view: '/modules/sermons',
                type: 'error',
                dismissable: true,
                id: 'sermon-media-invalid',
            }));
        };

        const getMediaForVersion = () => {
            return new Promise((resolve, reject) => {
                dispatch({
                    type: types.GET_PROJECT_MEDIA_PENDING,
                });

                function handleError(error) {
                    if (version.mediaUrl) {
                        dispatch({
                            type: types.GET_PROJECT_MEDIA_FAILED,
                        });
                        throwMediaError(error);
                        resolve(error);
                    } else {
                        reject(error);
                    }
                }

                function checkMedia(response) {
                    media = response.data.data;
                    if (typeof version === 'string') {
                        handleError(version);
                    } else {
                        resolve(media);
                        dispatch(clearAlert('sermon-media-invalid'));
                    }
                }

                return getMedia(projectId)
                    .then(checkMedia)
                    .catch(handleError);
            });
        };

        const handleErrorForProjectVersion = (error) => {
            throwMediaError(error);
        };

        const returnAllObjects = () => {
            dispatch({
                type: types.UPDATE_PROJECT_VERSION_SUCCESS,
                payload: version,
            });
            dispatch(checkProjectConfig(projectId));
            dispatch({
                type: types.CLEAR_CLOVER_OBJECTS,
            });
            dispatch({
                type: types.GET_PROJECT_MEDIA_SUCCESS,
                payload: media,
            });
        };

        return projectVersionApi
            .updateProjectVersionViaApi(projectVersionModel, projectId)
            .then(storeUpdatedVersion)
            .then(getMediaForVersion)
            .then(returnAllObjects)
            .catch(handleErrorForProjectVersion);
    };
}

export function deleteProjectVersion(projectId) {
    return function (dispatch) {
        dispatch({
            type: types.DELETE_PROJECT_VERSION_PENDING,
        });
        return projectVersionApi.deleteProjectViaApi(projectId)
            .then(() => {
                return Promise.all([
                    dispatch({
                        type: types.DELETE_PROJECT_VERSION_SUCCESS,
                    }),
                    dispatch(getProjects()),
                ]);
            })
            .catch((error) => {
                errorHandler(dispatch, error.response, types.DELETE_PROJECT_VERSION_FAILED);
            });
    };
}

export function publishProject(projectId, cid) {
    return function (dispatch) {
        dispatch({
            type: types.PUBLISH_PROJECT_PENDING,
        });
        return projectConfigApi.checkProjectConfig(projectId)
            .then((response) => {
                dispatch({
                    type: types.CHECK_PROJECT_CONFIG_SUCCESS,
                    payload: response.data.data,
                });
                if (response.data.data.length === 0) {
                    const fetchPages = () => {
                        return function () {
                            dispatch(getPagesforProject(projectId));
                        };
                    };
                    const fetchMenuItems = () => {
                        return function () {
                            dispatch(getProjectVersionMenuItems(projectId));
                        };
                    };

                    return projectConfigApi.publishProject(projectId)
                        .then((resp) => {
                            dispatch(fetchPages());
                            dispatch(fetchMenuItems());
                            dispatch({
                                type: types.PUBLISH_PROJECT_SUCCESS,
                                payload: resp.data.data,
                            });
                            dispatch({
                                type: types.SHOW_PUBLISH_NOTIFICATION,
                            });
                            dispatch(getLiveProjectConfig(cid));
                            setTimeout(() => {
                                dispatch({ type: types.HIDE_PUBLISH_NOTIFICATION });
                            }, 7000);
                        })
                        .catch((error) => {
                            errorHandler(dispatch, error.response, types.PUBLISH_PROJECT_FAILED);
                            if (error.response
                                && error.response.data
                                && error.response.data.data
                                && error.response.data.data.length > 0) {
                                dispatch({
                                    type: types.CHECK_PROJECT_CONFIG_SUCCESS,
                                    payload: error.response.data.data,
                                });
                            } else {
                                dispatch({
                                    type: types.CHECK_PROJECT_CONFIG_SUCCESS,
                                    payload:
                                        [{
                                            errorMessage: 'An error occurred; please try again soon or contact support.',
                                            errorTypeString: 'PUBLISH_FAILED',
                                            comment: 'Please contact support if error persists',
                                        }],
                                });
                            }
                        });
                }

                return dispatch({
                    type: types.PUBLISH_PROJECT_FAILED,
                });
            })
            .catch((error) => {
                errorHandler(dispatch, error.response, types.CHECK_PROJECT_CONFIG_FAILED);
            });
    };
}
